/**
 * Home-screen install helpers.
 *
 * Chrome/Android fires `beforeinstallprompt` once, very early — we capture it
 * at module load so any UI mounted later can still offer a real install button.
 * iOS Safari has no such event; there the only path is Share > Add to Home
 * Screen, so we detect it and show instructions instead.
 */

export type InstallCapability = "android" | "ios" | "none";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((l) => l());
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    notify();
    void import("./telemetry").then(({ trackInstallPrompt }) =>
      trackInstallPrompt("installed", { surface: "appinstalled", platform: "android" }),
    );
  });
}

export function subscribeInstallState(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const nav = navigator as Navigator & { standalone?: boolean };
  if (nav.standalone) return true;
  return window.matchMedia?.("(display-mode: standalone)").matches ?? false;
}

export function isIosSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const iosDevice =
    /iPhone|iPad|iPod/.test(ua) ||
    // iPadOS 13+ reports itself as a Mac; touch points give it away.
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  if (!iosDevice) return false;
  // Every iOS browser is WebKit, but only Safari exposes Add to Home Screen.
  return !/CriOS|FxiOS|EdgiOS|OPiOS|Instagram|FBAN|FBAV|Line\//.test(ua);
}

/** True in dev, iframes, and Lovable preview hosts, where installing is meaningless. */
export function inBlockedInstallContext(): boolean {
  if (typeof window === "undefined") return true;
  try {
    if (window.self !== window.top) return true;
  } catch {
    return true;
  }
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return true;
  if (host.startsWith("id-preview--") || host.startsWith("preview--")) return true;
  if (host.endsWith(".lovableproject.com") || host.endsWith(".lovableproject-dev.com")) return true;
  if (host.endsWith(".beta.lovable.dev")) return true;
  return false;
}

export function getInstallCapability(): InstallCapability {
  if (typeof window === "undefined") return "none";
  if (inBlockedInstallContext() || isStandalone()) return "none";
  if (deferred) return "android";
  if (isIosSafari()) return "ios";
  return "none";
}

/** Shows the native Android install sheet. Returns true when the user accepted. */
export async function promptInstall(surface = "unknown"): Promise<boolean> {
  const evt = deferred;
  if (!evt) return false;
  const { trackInstallPrompt } = await import("./telemetry");
  trackInstallPrompt("clicked", { surface, platform: "android" });
  try {
    await evt.prompt();
    const { outcome } = await evt.userChoice;
    trackInstallPrompt(outcome === "accepted" ? "accepted" : "dismissed", {
      surface,
      platform: "android",
    });
    if (outcome === "accepted") {
      deferred = null;
      notify();
      return true;
    }
  } catch {
    trackInstallPrompt("error", { surface, platform: "android" });
  }
  return false;
}
